/**
 * นโยบาย/ประกาศการทำงาน (พนักงาน) — อ่าน + กดรับทราบและยอมรับ (เก็บหลักฐาน)
 * ข้อตกลงร่วมบริษัท↔พนักงาน. ฉบับปรับปรุงใหม่ต้องรับทราบใหม่.
 */
import { useState } from 'react';
import { Clock, Timer, Plane, CalendarOff, FileText, CheckCircle2, ChevronDown, ShieldCheck } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useToast } from '../components/ui/toast';
import { usePolicies, useAckPolicy, type PolicyItem } from '../hooks';

const CAT = {
  time: { icon: Clock, label: 'เวลาทำงาน' },
  ot: { icon: Timer, label: 'OT' },
  leave: { icon: Plane, label: 'การลา' },
  holiday: { icon: CalendarOff, label: 'วันหยุด' },
  general: { icon: FileText, label: 'ทั่วไป' },
} as const;

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const parts = iso.slice(0, 10).split('-');
  const y = Number(parts[0]); const m = Number(parts[1]); const d = Number(parts[2]);
  const mth = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  if (!y || !m) return iso;
  return `${d} ${mth[m]} ${y + 543}`;
}

function PolicyCard({ p }: { p: PolicyItem }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const ack = useAckPolicy();
  const cat = CAT[p.category] ?? CAT.general;
  const Icon = cat.icon;
  const needAck = !p.acknowledged;

  async function doAck() {
    try { await ack.mutateAsync({ policy_id: p.id, version: p.version }); toast.success('รับทราบเรียบร้อย ขอบคุณครับ'); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  return (
    <div className={`card overflow-hidden ${needAck ? 'ring-1 ring-gold' : ''}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-start gap-3 p-4 text-left">
        <span className="w-9 h-9 rounded-xl bg-gold-wash text-gold-deep grid place-items-center shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-display font-semibold uppercase tracking-wider text-gold-deep">{cat.label}</span>
            {needAck
              ? <span className="text-[10px] bg-gold text-ink rounded-full px-2 py-0.5 font-medium">ต้องรับทราบ</span>
              : <span className="text-[10px] text-[#2f7d52] inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> รับทราบแล้ว</span>}
          </div>
          <p className="font-display font-semibold text-ink mt-0.5">{p.title}</p>
          <p className="text-xs text-warm-gray mt-0.5">
            {p.doc_code ? `${p.doc_code} · ` : ''}ฉบับที่ {p.version} · มีผล {fmtDate(p.effective_date)}
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted shrink-0 mt-1 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="text-sm text-ink whitespace-pre-line leading-relaxed border-t border-[color:rgba(13,11,10,.08)] pt-3">
            {p.body}
          </div>
          <div className="mt-4">
            {p.acknowledged ? (
              <div className="text-xs text-[#2f7d52] inline-flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> คุณรับทราบและยอมรับฉบับนี้แล้ว
              </div>
            ) : (
              <button onClick={doAck} disabled={ack.isPending} className="btn-cta w-full inline-flex items-center justify-center gap-2">
                <ShieldCheck className="w-4 h-4" /> {ack.isPending ? 'กำลังบันทึก…' : 'รับทราบและยอมรับ'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function Policies() {
  const { data, isLoading } = usePolicies();
  const items = data?.items ?? [];
  const pending = items.filter((p) => !p.acknowledged).length;

  return (
    <PageLayout title="นโยบายการทำงาน">
      <p className="text-sm text-warm-gray mb-3">
        ข้อตกลงร่วมระหว่างบริษัทกับพนักงาน — โปรดอ่านและกด “รับทราบและยอมรับ” ทุกฉบับ
        {pending > 0 && <span className="text-gold-deep font-medium"> · ยังไม่รับทราบ {pending} ฉบับ</span>}
      </p>
      {isLoading && <div className="card p-6 text-center text-muted">กำลังโหลด…</div>}
      {!isLoading && items.length === 0 && <div className="card p-6 text-center text-muted">ยังไม่มีประกาศ</div>}
      <div className="space-y-2.5">
        {items.map((p) => <PolicyCard key={p.id} p={p} />)}
      </div>
    </PageLayout>
  );
}
